// ─────────────────────────────────────────────────────────────
// Trips Screen — Lista e gestão de viagens
// ─────────────────────────────────────────────────────────────
import { ref, onValue, off, set }             from 'firebase/database';
import { ref as sRef, deleteObject }           from 'firebase/storage';
import { db, storage }                         from '@/firebase';
import { getState }                            from '@/store/tripState';
import { openModal }                           from '@/ui/modal';
import { toastSuccess }                        from '@/ui/toast';
import { fmt, diffDays, toArr, uid, initials,
         avatarColor, g, requireFields,
         sanitize }                            from '@/helpers';
import { uploadAttachment }                    from '@/services/trips.service';
import { logAction }                           from '@/services/logger.service';
import { TRIP_EMOJIS as EMOJIS,
         TRIP_COLORS as COLORS }               from '@/constants';
import type { Trip, Traveler }                 from '@/types';

// ── Keep reference to active listener so we can detach on logout ──
let _tripsListenerRef: ReturnType<typeof ref> | null = null;
const _tripCache = new Map<string, Trip>();

/** Detach the userTrips onValue listener — call before logout */
export function unsubscribeTrips(): void {
  if (_tripsListenerRef) {
    off(_tripsListenerRef);
    _tripsListenerRef = null;
  }
  _tripCache.clear();
}

export function loadTrips(): void {
  const { currentUser, isAdmin } = getState();
  if (!currentUser) return;
  // Clear cache on every explicit loadTrips() call so edits/creates show fresh data
  _tripCache.clear();
  const indexRef = ref(db, `userTrips/${currentUser.uid}`);
  off(indexRef);
  _tripsListenerRef = indexRef;
  onValue(indexRef, async (snap) => {
    const index   = (snap.val() ?? {}) as Record<string, boolean>;
    const tripIds = Object.keys(index);
    const grid    = document.getElementById('trips-grid');
    if (!grid) return;
    if (!tripIds.length) {
      _tripCache.clear();
      grid.innerHTML = `<div class="empty" style="grid-column:1/-1">
        Nenhuma viagem ainda.${isAdmin ? ' Crie a primeira!' : ' Crie uma nova viagem ou use um código de convite para entrar em uma existente.'}</div>`;
      return;
    }
    // Evict trips removed from the user's index
    for (const id of [..._tripCache.keys()]) {
      if (!index[id]) _tripCache.delete(id);
    }
    // Fetch only trips not yet cached
    const missing = tripIds.filter(id => !_tripCache.has(id));
    if (missing.length) {
      const { get } = await import('firebase/database');
      const snaps = await Promise.all(missing.map(id => get(ref(db, `trips/${id}`))));
      snaps.forEach((s, i) => {
        const t = s.val() as Trip | null;
        if (t) _tripCache.set(missing[i], t);
      });
    }
    const trips = tripIds.map(id => _tripCache.get(id)).filter(Boolean) as Trip[];
    grid.innerHTML = trips.map(t => {
      const travelers   = toArr(t.travelers ?? {});
      const avatarsHtml = travelers.slice(0, 5).map((tr: Traveler, i: number) =>
        `<div class="avatar" style="background:${avatarColor(i)};color:#fff">${initials(tr.name)}</div>`
      ).join('');
      const extra = travelers.length > 5
        ? `<div class="avatar" style="background:var(--bg3);color:var(--muted)">+${travelers.length - 5}</div>` : '';
      return `<div class="trip-card" style="--trip-color:${t.color ?? '#00A3E0'}" onclick="window.__openTrip('${t.id}')">
        <div class="trip-card-cover">${t.coverUrl ? `<img src="${t.coverUrl}" alt="">` : ''}
          <span class="trip-card-cover-emoji">${t.emoji ?? '✈️'}</span></div>
        <div class="trip-card-body">
          <div class="trip-card-title">${sanitize(t.name ?? 'Sem nome')}</div>
          <div class="trip-card-dates">${fmt(t.start)} → ${fmt(t.end)} · ${diffDays(t.start, t.end)} dias</div>
          <div class="trip-card-people">${avatarsHtml}${extra}</div>
        </div>
        ${isAdmin ? `<div class="trip-card-menu" onclick="event.stopPropagation()">
          <button class="icon-btn" onclick="window.__openTripModal('${t.id}')">✏</button>
          <button class="icon-btn del" onclick="window.__deleteTrip('${t.id}')">×</button></div>` : ''}
      </div>`;
    }).join('');
  });
}

export function openTripModal(editId?: string): void {
  if (editId) {
    import('firebase/database').then(({ get: getDb }) =>
      getDb(ref(db, `trips/${editId}`)).then(snap =>
        _showTripModal(editId, (snap.val() ?? {}) as Partial<Trip>)));
  } else { _showTripModal(undefined, {}); }
}

function _showTripModal(editId: string | undefined, trip: Partial<Trip>): void {
  const p = <K extends keyof Trip>(f: K, def: Trip[K] | string = ''): Trip[K] | string =>
    trip[f] !== undefined ? trip[f]! : def;
  const selEmoji = p('emoji', '✈️') as string;
  const selColor = p('color', '#378add') as string;
  // ps() — p() variant that sanitizes for use in HTML attribute values / innerHTML
  const ps = (f: keyof Trip, def = '') => sanitize(String(trip[f] ?? def));
  openModal(editId ? 'Editar viagem' : 'Nova viagem', `
    <div class="fg"><label>Nome da viagem</label><input id="f-tname" value="${ps('name')}"></div>
    <div class="r2">
      <div class="fg"><label>Data de início</label><input type="date" id="f-tstart" value="${ps('start')}"></div>
      <div class="fg"><label>Data de fim</label><input type="date" id="f-tend" value="${ps('end')}"></div>
    </div>
    <div class="fg"><label>Ícone</label>
      <div style="font-size:11px;color:var(--muted);margin-bottom:4px">Selecionado: <span id="selected-emoji-preview" style="font-size:18px">${sanitize(selEmoji)}</span></div>
      <input type="hidden" id="f-emoji" value="${sanitize(selEmoji)}">
      <div class="emoji-grid">${EMOJIS.map(e => `<div class="emoji-opt ${e===selEmoji?'selected':''}" onclick="window.__selectEmoji(this,'${e}')">${e}</div>`).join('')}</div>
    </div>
    <div class="fg"><label>Cor</label>
      <input type="hidden" id="f-color" value="${sanitize(selColor)}">
      <div class="color-swatches">${COLORS.map(c => `<div class="color-swatch ${c===selColor?'selected':''}" style="background:${c}" onclick="window.__selectColor(this,'${c}')"></div>`).join('')}</div>
    </div>
    <div class="fg"><label>Imagem de capa (opcional)</label>
      <div id="cover-preview-wrap" style="display:${p('coverUrl')?'block':'none'};margin-bottom:8px;position:relative">
        <img id="cover-preview-img" src="${ps('coverUrl')}" style="width:100%;height:90px;object-fit:cover;border-radius:var(--r)">
        <button type="button" onclick="window.__removeCoverPreview()" style="position:absolute;top:6px;right:6px;background:rgba(0,0,0,.55);color:#fff;border:none;border-radius:6px;padding:3px 8px;font-size:12px;cursor:pointer">✕</button>
      </div>
      <input type="hidden" id="f-cover-remove" value="0">
      <input type="file" id="f-cover" accept="image/*" onchange="window.__previewCover(this)">
      <div id="cover-progress-wrap" style="display:none;margin-top:6px">
        <div style="font-size:11px;color:var(--muted);margin-bottom:3px" id="cover-progress-label">Enviando...</div>
        <div style="height:4px;background:var(--bg3);border-radius:999px;overflow:hidden">
          <div id="cover-progress-fill" style="height:100%;width:0%;background:var(--lux-blue);border-radius:999px;transition:width .2s"></div>
        </div>
      </div>
    </div>`,
    async () => {
      const { currentUser } = getState();
      if (!currentUser) return;
      requireFields([{ id: 'f-tname', label: 'Nome da viagem' }]);
      const start = g('f-tstart');
      const end   = g('f-tend');
      if (start && end && end < start) throw new Error('A data de fim deve ser posterior à data de início.');
      const id = editId ?? uid();
      let coverUrl  = p('coverUrl', '') as string;
      let coverPath = p('coverPath', '') as string;
      if ((document.getElementById('f-cover-remove') as HTMLInputElement)?.value === '1') {
        if (coverPath) deleteObject(sRef(storage, coverPath)).catch(() => void 0);
        coverUrl = coverPath = '';
      }
      const fileInput = document.getElementById('f-cover') as HTMLInputElement;
      if (!coverUrl && fileInput?.files?.length) {
        const file  = fileInput.files[0];
        if (!file.type.startsWith('image/'))
          throw new Error('A capa deve ser uma imagem (JPG, PNG, WebP, etc.).');
        if (file.size > 5 * 1024 * 1024)
          throw new Error('A imagem de capa não pode ultrapassar 5 MB.');
        const fill  = document.getElementById('cover-progress-fill') as HTMLElement;
        const label = document.getElementById('cover-progress-label');
        const wrap  = document.getElementById('cover-progress-wrap');
        if (wrap) wrap.style.display = 'block';
        if (coverPath) deleteObject(sRef(storage, coverPath)).catch(() => void 0);
        const att = await uploadAttachment(id, 'covers', 'cover', file, pct => {
          if (fill)  fill.style.width    = pct + '%';
          if (label) label.textContent = `Enviando... ${pct}%`;
        });
        coverUrl = att.url; coverPath = att.path;
        if (label) label.textContent = 'Concluído ✓';
      }
      const obj: Partial<Trip> = {
        id, name: g('f-tname'), start: g('f-tstart'), end: g('f-tend'),
        emoji: g('f-emoji') || '✈️', color: g('f-color') || '#00A3E0',
        coverUrl, coverPath,
        travelers: trip.travelers??{}, destinos: trip.destinos??{},
        transporte: trip.transporte??{}, hoteis: trip.hoteis??{},
        agenda: trip.agenda??{}, checks: trip.checks??{},
        budgetLimit: trip.budgetLimit??0, cotacoes: trip.cotacoes??{},
        compras: trip.compras??{}, docs: trip.docs??{},
      };

      // 1. Write userTrips index FIRST (required by security rules before writing trips)
      await set(ref(db, `userTrips/${currentUser.uid}/${id}`), true);

      // 2. Auto-add creator as first traveler (only on new trips)
      if (!editId) {
        const creatorId = uid();
        obj.travelers = {
          [creatorId]: {
            id:    creatorId,
            name:  currentUser.displayName || currentUser.email,
            email: currentUser.email,
            uid:   currentUser.uid,
          } as import('@/types').Traveler,
        };
      }

      // 3. Link any existing travelers
      await Promise.all(
        Object.values(trip.travelers ?? {})
          .filter((tr: Traveler) => !!tr.uid)
          .map((tr: Traveler) => set(ref(db, `userTrips/${tr.uid}/${id}`), true))
      );

      // 3. Now write the trip data (rules will pass because userTrips entry exists)
      await set(ref(db, `trips/${id}`), obj);

      logAction(editId ? 'trip_updated' : 'trip_created', `Trip ${editId ? 'updated' : 'created'}: ${obj.name}`, { tripId: id });
      toastSuccess(editId ? 'Viagem atualizada!' : 'Viagem criada!');
      loadTrips();
    });
}

export function removeCoverPreview(): void {
  (document.getElementById('cover-preview-wrap') as HTMLElement).style.display = 'none';
  (document.getElementById('f-cover-remove') as HTMLInputElement).value = '1';
  (document.getElementById('f-cover') as HTMLInputElement).value = '';
}

export function previewCover(input: HTMLInputElement): void {
  if (!input.files?.length) return;
  const reader = new FileReader();
  reader.onload = e => {
    (document.getElementById('cover-preview-wrap') as HTMLElement).style.display = 'block';
    (document.getElementById('cover-preview-img') as HTMLImageElement).src = e.target?.result as string;
    (document.getElementById('f-cover-remove') as HTMLInputElement).value = '0';
  };
  reader.readAsDataURL(input.files[0]);
}

export function selectEmoji(el: HTMLElement, emoji: string): void {
  document.querySelectorAll('.emoji-opt').forEach(e => e.classList.remove('selected'));
  el.classList.add('selected');
  (document.getElementById('f-emoji') as HTMLInputElement).value = emoji;
  const p = document.getElementById('selected-emoji-preview');
  if (p) p.textContent = emoji;
}

export function selectColor(el: HTMLElement, color: string): void {
  document.querySelectorAll('.color-swatch').forEach(e => e.classList.remove('selected'));
  el.classList.add('selected');
  (document.getElementById('f-color') as HTMLInputElement).value = color;
}

export async function deleteTrip(tripId: string): Promise<void> {
  const { confirmModal } = await import('@/ui/modal');
  const ok = await confirmModal('Remover esta viagem permanentemente?');
  if (!ok) return;
  const { currentUser } = getState();
  if (!currentUser) return;

  // 1. Mark user null so any in-flight Firebase callbacks are silently ignored
  const { setUser } = await import('@/store/tripState');
  const savedUser = currentUser;
  setUser(null);

  // 2. Detach ALL active listeners before touching Firebase
  const { unsubscribeFromTrip } = await import('@/services/sync.service');
  const indexRef = ref(db, `userTrips/${currentUser.uid}`);
  off(indexRef);
  _tripsListenerRef = null;
  unsubscribeFromTrip();

  try {
    const { deleteTrip: svcDel } = await import('@/services/trips.service');
    await svcDel(tripId, currentUser.uid);
    logAction('trip_deleted', `Trip deleted: ${tripId}`, { tripId });
    toastSuccess('Viagem removida.');
  } finally {
    // 3. Restore user and re-attach trips listener
    setUser(savedUser);
    loadTrips();
  }
}

export function registerGlobals(): void {
  const w = window as unknown as Record<string, unknown>;
  w['__openTripModal']      = openTripModal;
  w['__deleteTrip']         = deleteTrip;
  w['__removeCoverPreview'] = removeCoverPreview;
  w['__previewCover']       = previewCover;
  w['__selectEmoji']        = selectEmoji;
  w['__selectColor']        = selectColor;
}
